import { useEffect, useCallback, useMemo, useReducer, useState, useRef } from 'react';
import classNames from 'classnames';
import './DataDesigner.less';
import {
  deleteDiagrams,
  saveSession as _saveSession,
  cancelSession,
  rollBack,
  rollForward,
  getRollBackPointsForSession,
  getRollForwardPointsForSession,
  ignoreValidationIssue,
  unignoreValidationIssue,
  hasChanges as _hasChanges,
  addDiagram,
  createDataTypeContext,
  submitCreateDataTypeContext,
  uploadSampleSet,
  pickSampleSet,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData/DataDesignerService';
import {
  DataDesignerSurfaceType,
  DataDesignSession,
  DataDiagramChangedNotification,
  isDataDiagramChangedNotification,
  newDiagramView,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData';
import { useRequest } from 'hooks/useRequest';
import { useClientEventServiceSubscription } from 'hooks/useClientEventServiceSubscription';
import { DesignerHeader, DesignerBody } from 'domains/designers/common';
import { isSetEvents, SetEvents } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ClientEvents';
import { DesignerLayout } from 'domains/designers/common/DesignerLayout';
import { Toolbox } from './Toolbox';
import { InfoPanel } from './InfoPanel';
import { IDesignerAction } from 'domains/designers/common/DesignerActions/DesignerAction';
import { DesignerPanel } from 'domains/designers/common';
import { Tabs, ITab } from 'components/tabs/Tabs';
import { TabPanel } from 'components/tabs/TabPanel';
import { DiagramPropertiesPanel } from './DataPropertiesPanel';
import { Align } from 'domains/designers/common/DesignerPanel/PanelToggler';
import { DesignerActions } from 'domains/designers/common';
import {
  getDefaultState,
  DataDesignerView,
  dataDesignerReducer,
  DataDesignerReducerAction,
  DataDesignerReducerState,
} from './DataDesignerReducer';
import { ValidationEntry } from 'components/HelpCenter/ValidationEntry';
import { BaseProxyObject } from '@decisions/server/Decisions/BaseProxyObject';
import { IDesignerSubAction } from 'domains/designers/common/DesignerActions/DesignerSubActions';
import {
  getDiagramById,
  getHelpCenterValidations,
  getNoDiagramNameMessage,
  getNoDiagramsMessage,
} from './DataDesignerUtils';
import { DataDesignerSurface } from './DataDesignerSurface';
import { convertToRelativeCoordinates, IPoint } from 'utils/GeometryUtils';
import { HasOpenState } from 'hooks/HasOpenState';
import { generateGuid } from 'utils/Guid';
import { isNullOrWhitespace } from 'utils/StringUtils';
import { ValidationIssue } from '@decisions/server/DecisionsFramework';
import { MappingContext } from '@decisions/server/DecisionsFramework/Design/Flow/Service';
import { newAddDiagramDisplayData } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData/DisplayData/AddDiagramDisplayData';
import { DataDesignerContext } from './DataDesignerContext';
import { useBeforeSavePromises } from 'hooks/useBeforeSavePromises';
import { PortalLoading } from 'components/PortalLoading';
import { HelpCenter } from 'components/HelpCenter/HelpCenter';
import { HelpCenterLocation } from 'components/HelpCenter/HelpCenterLocation';

export type ShowSaveDialogCallback = (title: string, message: string) => Promise<'save' | 'close' | undefined>;

/**
 * Both inputs and outputs can be edited by DataDesigner
 */
export type EditingDataType = 'inputs' | 'outputs';

export interface IInputDataDesignerProps {
  startSession: () => Promise<DataDesignSession>;
  /**
   * Required for editing mappings
   */
  mappingContext?: MappingContext;
  /**
   * When editing the design pattern entity
   */
  editDesignPatternId?: string;
  /**
   * Determines which view is currently shown
   *
   * @default 'Diagram View'
   */
  currentView?: DataDesignerView;
  /**
   * Called when designer has been closed
   */
  onClose?: () => void;
  /**
   * Handler for "Create New Data Type" in toolbox
   */
  onCreateTypeClick: () => void;
  /**
   * Determines whether to show Designer in overlay or not
   *
   * @default true
   */
  showInLayer?: boolean;
  /**
   * Adds indent around designer. When Flow/Rule/Form's inputs are being editing by Data Designer, indent should be added to hightlight that
   * there is main designer under this one.
   *
   * @default false
   */
  hasIndent?: boolean;
  /**
   * There is no dialogs in React part yet, so use this callback to provide the implementation from MVC side.
   *
   * When result is "undefined" - save dialog has been closed.
   */
  showSaveDialog: ShowSaveDialogCallback;
  /**
   * Provides handler to create type from inputs
   */
  onCreateTypeFromInputs: (contextId: string) => Promise<'create' | 'cancel'>;
  /**
   * Notify that session has been started
   */
  onSessionStarted?: (session: DataDesignSession) => void;
}

function isToolBoxVisible(currentTab: DataDesignerView): boolean {
  return currentTab === 'Diagram View';
}

function isHelpCenterSurfaceVisible(panels: ITab[], selectedPanel: string | null) {
  return panels.length === 0;
}

const designerTabs: ITab[] = [
  { label: 'Diagram View', value: 'Diagram View' },
  { label: 'List View', value: 'List View' },
];

/**
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function DataDesigner({
  startSession: startSessionProp,
  mappingContext,
  editDesignPatternId,
  showInLayer = true,
  hasIndent,
  currentView: currentViewProp = 'Diagram View',
  showSaveDialog,
  onClose,
  onCreateTypeClick,
  onSessionStarted,
  onCreateTypeFromInputs,
}: IInputDataDesignerProps) {
  const { makeRequest: startSession, response: session, /*error,*/ isLoading, setResponse } = useRequest(
    startSessionProp
  );

  const sessionId = session?.SessionId;
  const [surfaceType, setSurfaceType] = useState<DataDesignerSurfaceType>(DataDesignerSurfaceType.Input);
  const { makeRequest: saveSession, isLoading: isSaving } = useRequest(_saveSession);
  const [inputState, inputDispatch] = useReducer(dataDesignerReducer, getDefaultState(currentViewProp));
  const [outputState, outputDispatch] = useReducer(dataDesignerReducer, getDefaultState(currentViewProp));
  const [currentState, currentDispatch] =
    surfaceType === DataDesignerSurfaceType.Input ? [inputState, inputDispatch] : [outputState, outputDispatch];

  const commonDispatch = useCallback((surfaceType: DataDesignerSurfaceType, action: DataDesignerReducerAction) => {
    if (surfaceType === DataDesignerSurfaceType.Input) {
      inputDispatch(action);
    } else if (surfaceType === DataDesignerSurfaceType.Output) {
      outputDispatch(action);
    }
  }, []);

  const getSurfaceState = useCallback(
    (surfaceType: DataDesignerSurfaceType) => {
      return surfaceType === DataDesignerSurfaceType.Input ? inputState : outputState;
    },
    [inputState, outputState]
  );

  const designerBodyRef = useRef<HTMLDivElement | null>(null);
  const [selectedRightPanel, setCurrentRightPanel] = useState<string | null>(null);
  const [selectedLeftPanel, setCurrentLeftPanel] = useState<string | null>(
    isToolBoxVisible(inputState.currentView) ? 'Toolbox' : null
  );
  const [leftPanelWidth, setLeftPanelWidth] = useState(300);
  const [rightPanelWidth, setRightPanelWidth] = useState(300);
  const { isOpen: isDesignerLoading, setOpen: showLoading, setClosed: hideLoading } = HasOpenState();

  useEffect(() => {
    if (currentViewProp) {
      inputDispatch({ type: 'change-view', view: currentViewProp });
      outputDispatch({ type: 'change-view', view: currentViewProp });
    }
  }, [currentViewProp]);

  const hidePropertiesPanel = useCallback(() => {
    inputDispatch({ type: 'unselect-diagrams' });
    outputDispatch({ type: 'unselect-diagrams' });
  }, []);

  const handleEvent = useCallback((notification: BaseProxyObject) => {
    if (isDataDiagramChangedNotification(notification)) {
      const diagramChangedNotification = notification as DataDiagramChangedNotification;

      if (diagramChangedNotification.SurfaceType === DataDesignerSurfaceType.Input) {
        inputDispatch({
          type: 'update-diagram',
          diagram: diagramChangedNotification.Diagram,
        });
      } else {
        outputDispatch({
          type: 'update-diagram',
          diagram: diagramChangedNotification.Diagram,
        });
      }
    } else {
      console.warn(`Data Designer: Unknown event type: ${notification.__type}`);
    }
  }, []);

  const handleSetEvents = useCallback(
    (notification: SetEvents) => {
      for (const event of notification.Events) {
        handleEvent(event.OutputValue);
      }
    },
    [handleEvent]
  );

  useClientEventServiceSubscription(
    sessionId,
    `data-designer-${sessionId}`,
    useCallback(
      (notification: any) => {
        if (isSetEvents(notification)) {
          handleSetEvents(notification);
        } else {
          handleEvent(notification);
        }
      },
      [handleEvent, handleSetEvents]
    )
  );

  useEffect(() => {
    if (session != null) {
      inputDispatch({
        type: 'set-diagrams',
        diagrams: session.InputDiagrams,
      });
      outputDispatch({
        type: 'set-diagrams',
        diagrams: session.OutputDiagrams,
      });
      onSessionStarted?.(session);
    }
  }, [onSessionStarted, session]);

  useEffect(() => {
    startSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUndo = useCallback(
    async (rollbackName?: string) => {
      if (sessionId) {
        const { InputDiagrams, OutputDiagrams } = await rollBack({
          sessionId,
          rollbackName,
        });

        inputDispatch({
          type: 'set-diagrams',
          diagrams: InputDiagrams,
        });

        outputDispatch({
          type: 'set-diagrams',
          diagrams: OutputDiagrams,
        });

        hidePropertiesPanel();
      }
    },
    [hidePropertiesPanel, sessionId]
  );

  const getUndoSubActions = useCallback(async () => {
    if (sessionId) {
      const points = await getRollBackPointsForSession({
        sessionId,
      });

      return (points || []).map(
        point =>
          ({
            action: () => handleUndo(point),
            name: point,
            title: point,
          } as IDesignerSubAction)
      );
    }
  }, [sessionId, handleUndo]);

  const handleRedo = useCallback(
    async (rollbackName?: string) => {
      if (sessionId) {
        const { InputDiagrams, OutputDiagrams } = await rollForward({
          sessionId,
          rollbackName,
        });

        inputDispatch({
          type: 'set-diagrams',
          diagrams: InputDiagrams,
        });

        outputDispatch({
          type: 'set-diagrams',
          diagrams: OutputDiagrams,
        });

        hidePropertiesPanel();
      }
    },
    [hidePropertiesPanel, sessionId]
  );

  const getRedoSubActions = useCallback(async () => {
    if (sessionId) {
      const points = await getRollForwardPointsForSession({
        sessionId,
      });

      return (points || []).map(
        point =>
          ({
            action: () => handleRedo(point),
            name: point,
          } as IDesignerSubAction)
      );
    }
  }, [sessionId, handleRedo]);

  const confirmInputDataReplace = useCallback(
    async (action: () => void) => {
      if (
        (inputState.currentView === 'Diagram View' && inputState.diagrams.length > 0) ||
        // "List View" always have at least 1 emplty row, which added automatically,
        // so we don't need to show dialog for list view in this case
        (inputState.currentView === 'List View' && inputState.diagrams.length > 1)
      ) {
        Decisions.MessageDialog.Show(
          'Default Sample Set',
          'You are about to pick Default Sample Set that would replace your existing defined input data. Do you want to proceed?',
          ['Yes', 'No'],
          'Question',
          (name: any) => {
            if (name === 'Yes') {
              action();
            }
          }
        );
      } else {
        action();
      }
    },
    [inputState.currentView, inputState.diagrams.length]
  );

  const onSampleSetConfirmed = useCallback(
    async (pickedSampleSetId: string) => {
      if (!isNullOrWhitespace(pickedSampleSetId)) {
        const { InputDiagrams } = await pickSampleSet({ sessionId: sessionId, reportId: pickedSampleSetId });
        inputDispatch({ type: 'set-diagrams', diagrams: InputDiagrams });
      }
    },
    [sessionId]
  );

  const getPickOrCreateProps = useCallback(
    (isCreate: boolean) => {
      return {
        registrationType: 'Report',
        folderId: Decisions.Navigation.currentFolderId,
        forRegistrationId: session?.ForRegistrationId,
        hidePick: isCreate,
        hideCreate: !isCreate,
        handleCreatedRegistration: (registration: any) => {
          $DP.Designers.showDesigner({
            designerType: registration.RegistrationType,
            entityId: registration.RegistrationId,
            openInLayer: true,
            onClosed: () => {
              confirmInputDataReplace(async () => {
                onSampleSetConfirmed(registration.RegistrationId);
              });
            },
          });
        },
        handlePickedRegistration: (registrationId: string) => {
          confirmInputDataReplace(async () => {
            onSampleSetConfirmed(registrationId);
          });
        },
        showCreateDialog: $DP.Designers.Creation.showCreateDialog,
        runBaseAction: $DP.Common.Actions.ActionExecutor.runBaseAction,
        onDialogOptionsChanged: () => {},
        onItemChoosen: () => {},
      };
    },
    [confirmInputDataReplace, session, onSampleSetConfirmed]
  );

  const getSampleActions = useCallback(async () => {
    const actions: IDesignerSubAction[] = [];
    if (!DEV_SERVER) {
      actions.push({
        name: 'Pick',
        action: () => {
          $DP.EntityPicker.showPickOrCreateDialog(getPickOrCreateProps(false));
        },
      });
      actions.push({
        name: 'Create',
        action: () => {
          $DP.EntityPicker.showPickOrCreateDialog(getPickOrCreateProps(true));
        },
      });
      actions.push({
        name: 'Upload',
        action: () => {
          let selectedFileId: string | null = null;
          $DP.Common.FileUploadControl.showFileUploadDialog({
            id: generateGuid(),
            IsMultiFileUpload: false,
            allowedExtensions: ['csv', 'xls', 'xlsx'],
            dialogOkClick: () => {
              if (selectedFileId) {
                confirmInputDataReplace(async () => {
                  const { InputDiagrams } = await uploadSampleSet({
                    sessionId: sessionId,
                    fileId: selectedFileId ?? '',
                  });
                  inputDispatch({ type: 'set-diagrams', diagrams: InputDiagrams });
                });
              }
            },
            processingSelectedFilesComplete: (
              _: any,
              __: any,
              responseJSON: { fileName: string; success: boolean; Result: string; file_id: string }
            ) => {
              if (responseJSON.success) {
                selectedFileId = responseJSON.file_id;
              }
            },
          });
        },
      });
    }

    return actions;
  }, [confirmInputDataReplace, getPickOrCreateProps, sessionId]);

  const handleSave = useCallback(async () => {
    const result = await showSaveDialog('Save Inputs', 'Do you want to save your changes?');

    if (result === 'save') {
      hidePropertiesPanel();

      await saveSession({
        sessionId: sessionId,
      });
    }
  }, [hidePropertiesPanel, saveSession, sessionId, showSaveDialog]);

  const hasChangesCheck = useCallback(async () => {
    return await _hasChanges({
      sessionId: sessionId,
    });
  }, [sessionId]);

  // noop for this, because designer is intentionally handling cancel a different way than say, a property-grid,
  // but the cancel callback is intentionally required to remind devs for other situations.
  const editCompleteCancel = useCallback(() => {}, []);

  // allow any straggling setValues to get through before checking for changes here:
  const { addBeforeSavePromise, removeBeforeSavePromise, save: hasChanges } = useBeforeSavePromises({
    save: hasChangesCheck,
    cancel: editCompleteCancel,
  });

  const handleClose = useCallback(async () => {
    showLoading();
    try {
      const isChanged = await hasChanges();

      if (isChanged) {
        hideLoading();
        const result = await showSaveDialog(
          'Save Inputs',
          'You are about to close the designer. Do you want to save your changes?'
        );

        showLoading();

        // save dialog
        if (result == null) {
          return;
        }

        if (result === 'save') {
          hidePropertiesPanel();

          await saveSession({
            sessionId: sessionId,
          });
        }
      }

      // We need to remove all components before cancelSession call is completed
      // Otherwise some components will make calls to the server to get data from session, when session doesn't exist anymore (e.g PropertyGrid)
      setResponse(undefined);
      await cancelSession({
        sessionId: sessionId,
      });
      hideLoading();

      onClose?.();
    } finally {
      hideLoading();
    }
  }, [
    showLoading,
    hasChanges,
    sessionId,
    setResponse,
    hideLoading,
    onClose,
    showSaveDialog,
    hidePropertiesPanel,
    saveSession,
  ]);

  const refreshSelectedRightPanel = useCallback(
    (diagramsSurface: DataDesignerSurfaceType, newDiagramIds: string[]) => {
      if (newDiagramIds.length === 1) {
        setCurrentRightPanel('Properties');
        // reset right panel only if selected diagrams has been changed
        // it is needed to not hide "Properties" tab of Design Pattern, if it was visible
        // don't close "Properties" tab for Design pattern when click on surface. The same behavior in flow designer.
      } else if (newDiagramIds.length === 0 && getSurfaceState(diagramsSurface).selectedDiagramIds.length !== 0) {
        setCurrentRightPanel(null);
      }
    },
    [getSurfaceState]
  );

  const selectDiagrams = useCallback(
    (diagramsSurface: DataDesignerSurfaceType, diagramIds: string[]) => {
      if (diagramsSurface !== surfaceType) {
        // change surface if we select task from different surface
        setSurfaceType(diagramsSurface);
      }
      commonDispatch(diagramsSurface, { type: 'select-diagrams', diagramIds: diagramIds });
      refreshSelectedRightPanel(diagramsSurface, diagramIds);
    },
    [commonDispatch, refreshSelectedRightPanel, surfaceType]
  );

  const handleTypeDropped = useCallback(
    async (fullTypeName: string, droppedAtPoint: IPoint) => {
      const droppable = document.elementFromPoint(droppedAtPoint.x, droppedAtPoint.y);
      const diagramView = designerBodyRef.current?.querySelector('.dp-data-diagram-view') as HTMLElement;
      if (
        diagramView &&
        droppable &&
        (diagramView === droppable || droppable.closest('.dp-data-diagram-view') != null)
      ) {
        const point = convertToRelativeCoordinates(droppedAtPoint, diagramView);
        const input = await addDiagram({
          sessionId: sessionId,
          surfaceType,
          diagram: newAddDiagramDisplayData({
            IsList: false,
            FullTypeName: fullTypeName,
            View: newDiagramView({
              Left: point.x,
              Top: point.y,
            }),
          }),
        });
        if (surfaceType === DataDesignerSurfaceType.Input) {
          inputDispatch({
            type: 'add-diagram',
            input,
          });
          selectDiagrams(DataDesignerSurfaceType.Input, [input.Id]);
        } else {
          outputDispatch({
            type: 'add-diagram',
            input,
          });
          selectDiagrams(DataDesignerSurfaceType.Output, [input.Id]);
        }
      }
    },
    [selectDiagrams, sessionId, surfaceType]
  );

  const leftPanels: ITab[] = useMemo(() => {
    const panels: ITab[] = [];

    if (!!editDesignPatternId) {
      panels.push({ value: 'Info' });
    }

    if (isToolBoxVisible(inputState.currentView)) {
      panels.push({ value: 'Toolbox' });
    }

    return panels;
  }, [editDesignPatternId, inputState.currentView]);

  function createValidationEntry(surfaceType: DataDesignerSurfaceType, validation: ValidationIssue) {
    return new ValidationEntry({
      id: generateGuid(),
      title: validation.Reason,
      description: validation.Details,
      level: validation.Level,
      isBreakingIssue: validation.IsBreakingIssue,
      isIgnored: validation.IsIgnored,
      gotoAction: () => {
        selectDiagrams(surfaceType, [validation.SourceInfo]);
      },
      ignoreAction: (_, isIgnored) => {
        if (isIgnored) {
          unignoreValidationIssue({
            sessionId: sessionId,
            issue: validation,
            surfaceType: surfaceType,
          });
        } else {
          ignoreValidationIssue({
            sessionId: sessionId,
            issue: validation,
            surfaceType: surfaceType,
          });
        }
      },
    });
  }

  const validations = [
    ...getHelpCenterValidations(inputState).map(validation =>
      createValidationEntry(DataDesignerSurfaceType.Input, validation)
    ),
    ...getHelpCenterValidations(outputState).map(validation =>
      createValidationEntry(DataDesignerSurfaceType.Output, validation)
    ),
  ];

  const removeLastAddedRow = useCallback(
    (surfaceType: DataDesignerSurfaceType, state: DataDesignerReducerState) => {
      const lastAddedInputId = state.addedDiagramIdsInListView[state.addedDiagramIdsInListView.length - 1];
      const lastAddedInput = getDiagramById(state.diagrams, lastAddedInputId);
      if (lastAddedInput && !lastAddedInput.DataDescription.Name) {
        // Last row is automatically added to "List View" to allow user add input
        // so if name is not provided this row should be removed when switching to "Diagram View"
        deleteDiagrams({
          sessionId: sessionId,
          surfaceType,
          inputDataIds: [lastAddedInputId],
          ignoreChanges: true,
        });

        commonDispatch(surfaceType, { type: 'delete-diagrams', diagramIds: [lastAddedInputId] });
      }
    },
    [commonDispatch, sessionId]
  );

  const handleChangeTab = useCallback(
    async (_view: string) => {
      const view = _view as DataDesignerView;
      inputDispatch({ type: 'change-view', view });
      outputDispatch({ type: 'change-view', view });

      if (view === 'Diagram View') {
        removeLastAddedRow(DataDesignerSurfaceType.Input, inputState);
        removeLastAddedRow(DataDesignerSurfaceType.Output, outputState);
      }
    },
    [removeLastAddedRow, inputState, outputState]
  );

  const handleCreateDataType = useCallback(async () => {
    if (sessionId != null) {
      showLoading();
      let contextId = '';
      try {
        contextId = await createDataTypeContext({
          sessionId: sessionId,
          folderId: Decisions.Navigation.currentFolderId,
          surfaceType,
        });
      } finally {
        hideLoading();
      }

      const result = await onCreateTypeFromInputs(contextId);

      if (result === 'cancel') {
        return;
      }

      showLoading();
      try {
        const { InputDiagrams, OutputDiagrams } = await submitCreateDataTypeContext({
          sessionId: sessionId,
          dataTypeContextId: contextId,
        });

        if (surfaceType === DataDesignerSurfaceType.Input) {
          inputDispatch({ type: 'set-diagrams', diagrams: InputDiagrams });
        } else {
          outputDispatch({ type: 'set-diagrams', diagrams: OutputDiagrams });
        }
      } finally {
        hideLoading();
      }
    }
  }, [sessionId, surfaceType, onCreateTypeFromInputs, showLoading, hideLoading]);

  const handleDeleteDiagrams = useCallback(
    (diagramsSurface: DataDesignerSurfaceType, deletedDiagramIds: string[]) => {
      const surfaceState = getSurfaceState(diagramsSurface);

      if (surfaceState.selectedDiagramIds.length === 0) {
        return;
      }

      const diagramId = surfaceState.selectedDiagramIds[0];
      if (deletedDiagramIds.includes(diagramId)) {
        // hide right panel if it displayes properties of deleted diagram
        setCurrentRightPanel(null);
      }
    },
    [getSurfaceState]
  );

  const headerActions: IDesignerAction[] = useMemo(() => {
    return [
      { name: 'Save', action: handleSave },
      { action: () => handleUndo(), subActions: getUndoSubActions, name: 'Undo' },
      { action: () => handleRedo(), subActions: getRedoSubActions, name: 'Redo' },
    ];
  }, [getRedoSubActions, getUndoSubActions, handleRedo, handleSave, handleUndo]);

  const topBarActions = useMemo(() => {
    const actions: IDesignerAction[] = [{ action: handleCreateDataType, name: 'Create Type' }];

    if (session?.CanPickSampleSet && surfaceType === DataDesignerSurfaceType.Input) {
      actions.push({
        subActions: getSampleActions,
        name: 'Use Sample Set',
      });
    }

    return actions;
  }, [getSampleActions, handleCreateDataType, session?.CanPickSampleSet, surfaceType]);

  if (isLoading || session == null || !sessionId) {
    return null;
  }

  const isPropertiesPanelDisabled = (currentState.selectedDiagramIds ?? []).length !== 1 && !editDesignPatternId;

  return (
    <DataDesignerContext.Provider
      value={{
        mappingContext,
        noDiagramsMessage: getNoDiagramsMessage(surfaceType),
        noDiagramNameMessage: getNoDiagramNameMessage(surfaceType),
      }}
    >
      <DesignerLayout
        id={'dpc-data-designer'}
        className={classNames('dpc-data-designer', 'full-size')}
        hasIndent={hasIndent}
        showInLayer={showInLayer}
      >
        {isDesignerLoading && <PortalLoading showText={false} delay={1000} />}
        <DesignerHeader
          isLoading={isSaving}
          loadingText={'Saving...'}
          title={session.Title}
          breadcrumbs={session.Path}
          onClose={handleClose}
        >
          <DesignerActions theme="orange" actions={headerActions} autoCollapse={false} />
          <Tabs
            className="dp-designer-tabs"
            onTabSelected={handleChangeTab}
            currentValue={inputState.currentView}
            tabs={designerTabs}
          />
        </DesignerHeader>
        <DesignerBody ref={designerBodyRef}>
          <DesignerPanel
            width={leftPanelWidth}
            minWidth={200}
            maxWidth={500}
            onWidthChanged={setLeftPanelWidth}
            align={Align.left}
            tabs={leftPanels}
            currentTabId={selectedLeftPanel}
            validations={validations}
            onTabSelected={setCurrentLeftPanel}
          >
            <TabPanel tabId="Info" currentTabId={selectedLeftPanel}>
              <InfoPanel sessionId={sessionId} />
            </TabPanel>
            <TabPanel tabId="Toolbox" currentTabId={selectedLeftPanel}>
              <Toolbox onTypeDropped={handleTypeDropped} onCreateTypeClick={onCreateTypeClick} />
            </TabPanel>
          </DesignerPanel>
          <div className="dpc-data-designer__surface-wrapper">
            <div className="dpc-data-designer__top-bar">
              {session.CanEditOutputData && (
                <Tabs
                  className={'dpc-data-designer__top-bar-tabs'}
                  currentValue={DataDesignerSurfaceType[surfaceType]}
                  tabs={[
                    {
                      value: DataDesignerSurfaceType[DataDesignerSurfaceType.Input],
                      label: `Input Data (${inputState.diagrams.length})`,
                    },
                    {
                      value: DataDesignerSurfaceType[DataDesignerSurfaceType.Output],
                      label: `Output Data (${outputState.diagrams.length})`,
                    },
                  ]}
                  onTabSelected={value => {
                    const surfaceTypeValue = DataDesignerSurfaceType[value as keyof typeof DataDesignerSurfaceType];
                    setSurfaceType(surfaceTypeValue);
                  }}
                />
              )}

              <div className="dpc-data-designer__top-bar-actions">
                <DesignerActions theme="white" actions={topBarActions} autoCollapse={false} />
              </div>
            </div>
            <DataDesignerSurface
              sessionId={session.SessionId}
              state={currentState}
              dispatch={currentDispatch}
              surfaceType={surfaceType}
              onSelectionChanged={refreshSelectedRightPanel}
              onDeleteDiagrams={handleDeleteDiagrams}
              showInputColumn={session.ShowInputColumn}
            >
              {isHelpCenterSurfaceVisible(leftPanels, selectedLeftPanel) && (
                <HelpCenter entries={validations} location={HelpCenterLocation.DataDesigner} isOnSurface newStyle />
              )}
            </DataDesignerSurface>
          </div>
          <DesignerPanel
            width={rightPanelWidth}
            minWidth={200}
            maxWidth={500}
            onWidthChanged={setRightPanelWidth}
            align={Align.right}
            tabs={[{ value: 'Properties', disabled: isPropertiesPanelDisabled }]}
            currentTabId={selectedRightPanel}
            validations={validations}
            onTabSelected={setCurrentRightPanel}
          >
            {!isPropertiesPanelDisabled && (
              <TabPanel currentTabId={selectedRightPanel} tabId="Properties">
                <DiagramPropertiesPanel
                  sessionId={sessionId}
                  diagramId={currentState.selectedDiagramIds?.[0]}
                  surfaceType={surfaceType}
                  currentView={inputState.currentView}
                  addBeforeSavePromise={addBeforeSavePromise}
                  removeBeforeSavePromise={removeBeforeSavePromise}
                />
              </TabPanel>
            )}
          </DesignerPanel>
        </DesignerBody>
      </DesignerLayout>
    </DataDesignerContext.Provider>
  );
}
